import React, { useState, useRef, useEffect } from 'react';
import './RegisterModal.css';
import { API_URL } from '../services/api';

const API = API_URL;

function OtpInput({ value, onChange, disabled }) {
  const r0 = useRef(null), r1 = useRef(null), r2 = useRef(null);
  const r3 = useRef(null), r4 = useRef(null), r5 = useRef(null);
  const refs = [r0, r1, r2, r3, r4, r5];
  const digits = value.padEnd(6, '').split('').slice(0, 6);

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      if (i > 0) refs[i - 1].current?.focus();
    }
  };

  const handleChange = (i, e) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1);
    if (!ch) return;
    const next = value.slice(0, i) + ch + value.slice(i + 1);
    onChange(next.slice(0, 6));
    if (i < 5) refs[i + 1].current?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text) { onChange(text); refs[Math.min(text.length, 5)].current?.focus(); }
    e.preventDefault();
  };

  return (
    <div className="rm-otp-row">
      {refs.map((ref, i) => (
        <input key={i} ref={ref} className={`rm-otp-box${digits[i] ? ' rm-otp-filled' : ''}`}
          type="text" inputMode="numeric" maxLength={1}
          value={digits[i] || ''} disabled={disabled}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste} />
      ))}
    </div>
  );
}

export default function ForgotPasswordModal({ onClose }) {
  const [step, setStep]         = useState(1); // 1=email, 2=otp+password, 3=success
  const [email, setEmail]       = useState('');
  const [otp, setOtp]           = useState('');
  const [newPw, setNewPw]       = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to send code.'); return; }
      setStep(2);
      setResendCooldown(60);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { setError('Enter all 6 digits.'); return; }
    if (newPw.length < 8 || !/[A-Z]/.test(newPw) || !/[a-z]/.test(newPw) || !/\d/.test(newPw)) {
      setError('Password must be 8+ chars with uppercase, lowercase, and a number.');
      return;
    }
    if (newPw !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: otp, new_password: newPw }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Reset failed.'); return; }
      setStep(3);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true); setError('');
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setOtp('');
      setResendCooldown(60);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const STEPS = ['Enter email', 'Reset password', 'Done'];

  return (
    <div className="rm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rm-modal">

        {/* Header */}
        <div className="rm-header">
          <div className="rm-header-left">
            <div className="rm-header-icon">
              <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="#C8102E" strokeWidth="1.8"/>
                <path d="M7 11V7a5 5 0 0110 0v4" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <h2 className="rm-title">Reset your password</h2>
              <p className="rm-subtitle">We'll send a code to your registered email</p>
            </div>
          </div>
          <button className="rm-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 20 20" fill="none" width="20" height="20">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        {step < 3 && (
          <div className="rm-steps">
            {STEPS.map((label, i) => (
              <React.Fragment key={i}>
                <div className={`rm-step-dot ${step === i + 1 ? 'rm-dot-active' : step > i + 1 ? 'rm-dot-done' : ''}`}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <span className={`rm-step-label ${step === i + 1 ? 'rm-label-active' : ''}`}>{label}</span>
                {i < 2 && <div className={`rm-step-line ${step > i + 1 ? 'rm-line-done' : ''}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rm-error">
            <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
              <circle cx="10" cy="10" r="9" stroke="#C8102E" strokeWidth="1.5"/>
              <line x1="10" y1="6" x2="10" y2="11" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round"/>
              <circle cx="10" cy="14" r="1" fill="#C8102E"/>
            </svg>
            {error}
          </div>
        )}

        {/* Step 1 — email */}
        {step === 1 && (
          <form className="rm-form" onSubmit={handleSendCode} noValidate>
            <div className="rm-field">
              <label className="rm-label">Email Address</label>
              <input className="rm-input" type="email" placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)} autoFocus />
              <p className="rm-hint">Enter the email you registered with. We'll send a 6-digit reset code.</p>
            </div>
            <button type="submit" className="rm-btn-primary" disabled={loading}>
              {loading ? <span className="rm-spinner" /> : 'Send Reset Code'}
            </button>
          </form>
        )}

        {/* Step 2 — OTP + new password */}
        {step === 2 && (
          <form className="rm-form" onSubmit={handleReset} noValidate>
            <div className="rm-field rm-otp-section">
              <svg className="rm-otp-icon" viewBox="0 0 24 24" fill="none" width="40" height="40">
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p className="rm-otp-desc">Enter the 6-digit code sent to <strong>{email}</strong></p>
              <OtpInput value={otp} onChange={setOtp} disabled={loading} />
              <div className="rm-resend">
                <span>Didn't receive it?</span>
                {resendCooldown > 0
                  ? <span className="rm-resend-wait">Resend in {resendCooldown}s</span>
                  : <button type="button" className="rm-link" onClick={handleResend} disabled={loading}>Resend code</button>
                }
              </div>
            </div>

            <div className="rm-field">
              <label className="rm-label">New Password</label>
              <div className="rm-pw-wrap">
                <input className="rm-input" type={showPw ? 'text' : 'password'}
                  placeholder="Min 8 chars, uppercase, number"
                  value={newPw} onChange={e => setNewPw(e.target.value)} />
                <button type="button" className="rm-pw-toggle" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
                  {showPw
                    ? <svg viewBox="0 0 20 20" fill="none" width="17" height="17"><path d="M3 3l14 14M12.45 12.45A3 3 0 017.55 7.55M8.7 5.1A8.7 8.7 0 0110 5c5 0 8 5 8 5a15.3 15.3 0 01-2.7 3.3M6.5 6.5A14.5 14.5 0 002 10s3 5 8 5a8.6 8.6 0 004.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    : <svg viewBox="0 0 20 20" fill="none" width="17" height="17"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.5"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/></svg>
                  }
                </button>
              </div>
            </div>

            <div className="rm-field">
              <label className="rm-label">Confirm New Password</label>
              <input className="rm-input" type={showPw ? 'text' : 'password'}
                placeholder="Re-enter new password"
                value={confirm} onChange={e => setConfirm(e.target.value)} />
              {confirm && (
                <p className={`rm-match-hint ${newPw === confirm ? 'rm-match-ok' : 'rm-match-no'}`}>
                  {newPw === confirm ? '✓ Passwords match' : '✗ Passwords do not match'}
                </p>
              )}
            </div>

            <button type="submit" className="rm-btn-primary" disabled={loading || otp.length < 6}>
              {loading ? <span className="rm-spinner" /> : 'Set New Password'}
            </button>

            <button type="button" className="rm-back" onClick={() => { setStep(1); setOtp(''); setError(''); }}>
              ← Back
            </button>
          </form>
        )}

        {/* Step 3 — success */}
        {step === 3 && (
          <div className="rm-success">
            <div className="rm-success-icon">
              <svg viewBox="0 0 64 64" fill="none" width="72" height="72">
                <circle cx="32" cy="32" r="30" fill="rgba(200,16,46,0.06)" stroke="#C8102E" strokeWidth="2"/>
                <polyline points="18 32 27 42 46 22" stroke="#C8102E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="rm-success-title">Password updated!</h3>
            <p className="rm-success-body">
              Your password has been reset successfully.<br />
              You can now sign in with your new password.
            </p>
            <button className="rm-btn-primary" onClick={onClose}>
              Back to Sign In
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
